import { useCallback, useContext, useEffect, useState } from "react";
import Loader from "../../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import { Grid2 } from "@mui/material";
import StyledGridComp from "../../components/StyledGridComp";
import { MenuContext } from "../../App";
import SearchedComponent from "../../components/SearchedComponent";
import { fetchAllSiteManagementService } from "../../services/siteManagamentService";
import useDebounceSearch from "../../hooks/useDebounceSearch";

const SiteManagement = () => {
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);
  const [canAddSite, setCanAddSite] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const navigate = useNavigate();

  const { systemActions } = useContext(MenuContext);

  const access_token = localStorage?.getItem("access_token");
  const role = localStorage.getItem("role");
  const roleId = localStorage.getItem("roleId");

  const debounceQuery = useDebounceSearch(searchedValue, 500);

  useEffect(() => {
    if (page >= 0 && pageSize && access_token) {
      fetchAllSiteManagementData(page, pageSize, access_token, debounceQuery);
    }
  }, [pageSize, page, debounceQuery]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin") {
      let canAdd = false;
      const createFound = systemActions?.find(
        (item) => item?.name === "CanAddSites"
      );
      if (createFound) {
        canAdd = true;
      }
      setCanAddSite(canAdd);
    } else if (role === "Admin") {
      setCanAddSite(true);
    }
  }, [systemActions]);

  const columns = [
    {
      field: "siteNumber",
      headerName: "Site Number",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Site Number"}</div>,
      renderCell: (params) => {
        const siteNumber = params.row.siteNumber;
        const id = params.row.id;

        return (
          <button
            className="bread-crumbs-link grid-nav-btn"
            onClick={() => {
              navigate("/addUpdate-site-management", {
                state: {
                  id: id,
                },
              });
            }}
          >
            Site {siteNumber}
          </button>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Name"}</div>,
      renderCell: (params) => {
        const name = params.row.name;

        return <span className="data-font">{name}</span>;
      },
    },
    {
      field: "address1",
      headerName: "Address",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Address"}</div>,
      renderCell: (params) => {
        const address1 = params.row.address1;
        return <span className="data-font">{address1}</span>;
      },
    },
    {
      field: "city",
      headerName: "City",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"City"}</div>,
      renderCell: (params) => {
        const city = params.row.city;

        return <span className="data-font">{city}</span>;
      },
    },
    {
      field: "countryName",
      headerName: "Country",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Country"}</div>,
      renderCell: (params) => {
        const countryName = params.row.countryName;

        return <span className="data-font">{countryName}</span>;
      },
    },
    {
      field: "primaryContact",
      headerName: "Primary Contact",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Primary Contact"}</div>
      ),
      renderCell: (params) => {
        const primaryContact = params.row.primaryContact;
        return <span className="data-font">{primaryContact}</span>;
      },
    },

    {
      field: "phoneNumber",
      headerName: "Phone Number",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Phone Number"}</div>,
      renderCell: (params) => {
        const phoneNumber = params.row.phoneNumber;
        return <span className="data-font">{phoneNumber}</span>;
      },
    },
    {
      field: "isActive",
      headerName: "Active",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Active"}</div>,
      renderCell: (params) => {
        const isActive = params.row.isActive;
        return <span className="data-font">{isActive ? "True" : "False"}</span>;
      },
    },
    {
      field: "isDrugOrderingEnabled",
      headerName: "Drug Ordering Enabled",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Drug Ordering Enabled"}</div>
      ),
      renderCell: (params) => {
        const isDrugOrderingEnabled = params.row.isDrugOrderingEnabled;
        return (
          <span className="data-font">
            {isDrugOrderingEnabled ? "True" : "False"}
          </span>
        );
      },
    },
    {
      field: "isRandomizationOpen",
      headerName: "Randomization Open",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Randomization Open"}</div>
      ),
      renderCell: (params) => {
        const isRandomizationOpen = params.row.isRandomizationOpen;
        return (
          <span className="data-font">
            {isRandomizationOpen ? "True" : "False"}
          </span>
        );
      },
    },
    {
      field: "isScreeningOpen",
      headerName: "Screening Open",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Screening Open"}</div>
      ),
      renderCell: (params) => {
        const isScreeningOpen = params.row.isScreeningOpen;
        return (
          <span className="data-font">
            {isScreeningOpen ? "True" : "False"}
          </span>
        );
      },
    },
  ];

  const fetchAllSiteManagementData = async (
    page,
    pageSize,
    access_Token,
    debouncedSearch = ""
  ) => {
    setLoad(true);
    try {
      const res = await fetchAllSiteManagementService(
        page + 1,
        pageSize,
        access_Token,
        roleId,
        debouncedSearch
      );
      if (res?.status === 200) {
        setRowData(res?.data?.result);
        setRowLength(res?.data?.totalCount);
        setLoad(false);
      } else if (res?.status == 401) {
        navigate("/unauthorize");
      }
    } catch (err) {
      setLoad(false);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const handleSearch = useCallback((e) => {
    setSearchedValue(e.target.value);
  }, []);

  return (
    <>
      <div className="content-body">
        <p
          className="bread-crumbs-link-container"
          style={{ fontWeight: "600" }}
        >
          <Link className="bread-crumbs-link" to="/">
            Home
          </Link>{" "}
          |{" "}
          <Link className="bread-crumbs-link" to="/site-management">
            Site Management
          </Link>
          -{" "}
          <b style={{ color: "gray" }}>
            {localStorage?.getItem("studyName")}
          </b>
        </p>
        <Grid2
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", margin: "0 auto" }}
        >
          <Grid2 item md={6}>
            <p className="page-heading">Site Management</p>
          </Grid2>
          <Grid2 item md={6}>
            <div className="search-bar-container">
              <SearchedComponent
                handleSearch={handleSearch}
                placeholder="Sites"
                searchedValue={searchedValue}
              />
              <button
                className="create-btn-outlined"
                onClick={() => navigate("/bulk-edit-site")}
              >
                Bulk Edit Site
              </button>
              {canAddSite ? (
                <button
                  className="create-btn"
                  onClick={() => navigate("/addUpdate-site-management")}
                >
                  Add New Site
                </button>
              ) : null}
            </div>
          </Grid2>
        </Grid2>

        {load ? (
          <Loader />
        ) : (
          <StyledGridComp
            rows={rowData}
            columns={columns}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            length={rowLength}
          ></StyledGridComp>
        )}

        <Grid2
          alignItems={"end"}
          justifyContent="end"
          container
          sx={{ marginTop: "2%" }}
        >
          <Grid2 item md={6}>
            <button className="create-btn">Back</button>
          </Grid2>
        </Grid2>
      </div>
    </>
  );
};

export default SiteManagement;
